import { Injectable } from '@angular/core';
import { first, flatMap, map, mergeMap, switchMap, takeUntil, tap } from 'rxjs/operators';
import { BehaviorSubject, EMPTY, from, Observable, of, Subscriber } from 'rxjs';
import * as dayjs from 'dayjs';
import * as utc from 'dayjs/plugin/utc'; // import plugin
import * as timezone from 'dayjs/plugin/timezone'; // import plugin

import { CompassAPICartService } from 'src/app/shared/api-compass/compass-api-cart.service';
import { CompassAPIBookingAdditionalInfoFormService } from 'src/app/shared/api-compass/compass-api-booking-additional-info-form.service';
import { CompassAPIProductsService } from 'src/app/shared/api-compass/compass-api-products.service';
import { CompassAPIProductPricingOptionsService } from 'src/app/shared/api-compass/compass-api-product-pricing-options.service';
import { ConfigService } from 'src/app/shared/services/config.service';
import { SortService } from 'src/app/shared/services/sort.service';
import { StorageService } from './storage.service';

import { CartItem } from '../models/cart-item.interface';
import { Cart } from '../models/cart.interface';
import { Order } from '../models/order.interface';
import { ProductPricingOption } from '../models/product-pricing-option.interface';
import { ProductScheduleOccurrence } from '../models/product-schedule-occurrence.interface';
import { SharedModule } from '../shared.module';



const _log: boolean = false;

@Injectable()
export class CartService {

	private _cart: Cart = null;
	private _cart_Source = new BehaviorSubject<Cart>(null);
	public cart$ = this._cart_Source.asObservable();
	private _cartDefault: Cart = {
		cartTotal: 0,
		items: []
	};

	private _order: Order = null;
	private _order_Source = new BehaviorSubject<Order>(null);
	public order$ = this._order_Source.asObservable();

	public baseUrl: string;
	public cartItems$: Observable<any>;



	constructor(
		private _compassAPCartService: CompassAPICartService,
		private _compassAPIBookingAdditionalInfoFormService: CompassAPIBookingAdditionalInfoFormService,
		private _compassAPIProductsService: CompassAPIProductsService,
		private _compassAPIProductPricingOptionsService: CompassAPIProductPricingOptionsService,
		private _configService: ConfigService,
		private _sortService: SortService,
		private _storageService: StorageService,
	) {
		//console.log('CartService.constructor');
		dayjs.extend(utc);
		dayjs.extend(timezone);

		this.baseUrl = this._configService.urls.products.excursions;

		this.checkCartStorage();
		//this.checkOrderStorage();
	}


	/**
	 * return a new instance of cartDefault
	 * @returns
	*/
	public getCartDefault(){
    var cart = JSON.parse(JSON.stringify(this._cartDefault));
		return cart;
	}


	/**
	 * check cart storage and create if null
	*/
	public checkCartStorage(){
		//todo-jk: IF LOGGEDIN, CHECK API, ELSE GET FROM LOCALSTORAGE

		let _storageCart = this._storageService.getCart();
		if(!_storageCart){
			_storageCart = this.getCartDefault();
			this.setCart(_storageCart);
		}

		this._cart = _storageCart;
		this._cart_Source.next(this._cart);
	}

	// public getCartApi(){
	// 	this._compassAPCartService.getCarts()
	// 		.subscribe(res => {
	// 			console.log('getCartApi.res:', res);
	// 		});
	// }
	public loadCart(){
		this._compassAPCartService.loadCustomerCart().subscribe((res: any) => {
			if(!res.error) {
				this.setCart(res);
			}
		});
	}

	public getCart(){
		if(_log) console.log('CartService.getCart()', this._cart);

		if(!this._cart) {
			this._cart = this.getCartDefault();
		}
		return this._cart;
	}

	public setCart(cart: Cart){
		if(_log) console.log('CartService.setCart()', cart);
		this._cart = cart;
		this._cart_Source.next(this._cart);
		this._storageService.setCart(this._cart);
	}

	public clearCart(){
		if(_log) console.log('CartService.clearCart()');
		this._cart = this.getCartDefault();
		this._cart_Source.next(this._cart);
		this._storageService.unSetCart();
		return this._cart;
	}


	//SYNC Call that will end before api calls complete.
	//This should return and Observable so that you can the async pipe in the html

	/**
	 * Process Cart as Observable
	 * Cart data is incomplete. Requires calls for Product info and ProductPricingOption info
	 * @param cart
	 * @returns
	*/
	public processCartObs(): Observable<any> {
		return this.cart$.pipe(switchMap((cart: Cart) => {
			return cart?of(this.processCart(cart)):EMPTY;
		}))
	}

	public processCart(c: Cart) {
		const cart = {...c};
		//console.log('CartService.processCart', cart);
		cart.cartTotal = 0;
		if(cart?.items) {
			cart.items.forEach(async item => {
			const product = await this._compassAPIProductsService.getProductByIdPromise(item.productId);
			if(product) {
				item.productUrl = this.baseUrl + '/' + product.destination.region.slug + '/' + product.destination.slug + '/' + product.slug;
				item.productUseName = (product.displayName) ? product.displayName : product.name;
				item.productImageUrl = (product?.assets && product.assets.length > 0) ? product.assets[0].url : this._configService.images.defaultImage;

				let findSchedule = product.schedules.find(({id}) => id == item.productScheduleId);
				//console.log('CartService.processCartObs.findSchedule:', findSchedule);
				if(findSchedule) item.productScheduleUseName = (findSchedule.displayName) ? findSchedule.displayName : findSchedule.name;

				item.duration = product.duration;
				//item.startTime = findSchedule.startTime;
				item.startTime = findSchedule.startTime;
				item.startTimeUtc = item.startTime ? dayjs(item.startTime).utc().format('h:mm a') : null;
				//console.log('startTime:', item.startTime, '->', item.startTimeUtc);

				item.otherStartTime = findSchedule.otherStartTime;

			}

			item.itemTotal = 0;
			item.pricingOptions.forEach(async option => {
					//console.log('---- CartService.processCartObs.cart.item.option', option);

					const pricingOption = await this._compassAPIProductPricingOptionsService.getProductPricingOptionByIdPromise(option.productPricingOptionId);
					//console.log('CartService.processCartObs.pricingOption:', pricingOption);
          if(pricingOption != null){
            option.useName = (pricingOption.displayName) ? pricingOption.displayName : pricingOption.name;
            option.displayPrice = pricingOption.displayPrice;
          }


					this.updateCartTotals(cart);

				});

			});

			//console.log('CartService.processCart.cart:', cart);

			return cart;
		}

		return null;
	}



	/**
	 * Update cart totals.
	 * This logic didn't work well in the ^^observable^^. The math was never right for CartTotal
	 * @param cart
	 * @returns
	*/
	public updateCartTotals(cart: Cart) {
		//console.log('CartService.updateCartTotals.cart:', cart);
		if(!cart) return;
		cart.cartTotal = 0;
		//console.group('CartService.updateCartTotals()');
		if(cart?.items && cart.items.length > 0) {
			cart.items.forEach(item => {
				item.itemTotal = 0;
				item.pricingOptions.forEach(option => {
					item.itemTotal += (option.displayPrice * option.quantity);
				});
			//	console.log('CartService.updateCartTotals.item.itemTotal:', item.itemTotal);
				cart.cartTotal += item.itemTotal;
				//console.log('CartService.updateCartTotals.cartTotal:', cart.cartTotal);
			});
		}
		//console.groupEnd();
		return cart;
	}





	/**
	 * Sort cart items by date
	 * @param cart
	 * @returns
	*/
	public sortCartItems(cart: Cart) {
		if(cart?.items && cart.items.length > 1) {
			cart.items = this._sortService.sortByDate(cart.items, 'date');
		}
		return cart;
	}





	/**
	 * Remove pricing option from cart, clear out occurrence if options empty, clear out items if occurrences empty
	 * @param pricingOption
	 * @param occurrence
	 * @returns
	*/
	public removeOptionFromCart(pricingOption: ProductPricingOption,  occurrence: ProductScheduleOccurrence) {
		// console.group('CartService.removeOptionFromCart()');
		// console.log('CartService.pricingOption:', pricingOption);
		// console.log('CartService.occurrence:', occurrence);
		// console.log('CartService.this._cart:', this._cart);

		let lookForPricingOptionId = (pricingOption.productPricingOptionId) ? pricingOption.productPricingOptionId : pricingOption.id,
			tempCart: Cart = this._cart ? JSON.parse(JSON.stringify(this._cart)) : null,
			foundOccurrenceInCart = tempCart.items.find(item => item.productScheduleId == occurrence.productScheduleId && item.date == occurrence.date);

		// console.log('CartService.lookForPricingOptionId:', lookForPricingOptionId);
		// console.log('CartService.tempCart:', tempCart);
		// console.log('CartService.foundOccurrenceInCart:', foundOccurrenceInCart);

		foundOccurrenceInCart.pricingOptions = foundOccurrenceInCart.pricingOptions.filter(item => item.productPricingOptionId != lookForPricingOptionId);
		//console.log('CartService.foundOccurrenceInCart.pricingOptions:', foundOccurrenceInCart.pricingOptions);

		//DON'T REMOVE THE ENTIRE OCCURRENCE, UNLESS ALL OPTIONS ARE EMPTY
		if(foundOccurrenceInCart.pricingOptions.length == 0) {
			occurrence.alreadyInCart = false;
			tempCart.items = tempCart.items.filter(item => item.productScheduleId != foundOccurrenceInCart.productScheduleId);
		}

		if(_log) console.log('CartService.removeOptionFromCart.tempCart.items:', tempCart.items);
		if(tempCart.items.length == 0) {
			tempCart.items = null;//keep rest of cart intact
		}

		if(_log) console.log('CartService.removeOptionFromCart.cart:', tempCart);
		//console.groupEnd();

		return tempCart;

	}



	public deleteFromCart(deleteItem: CartItem) {
		if(_log) console.log('CartService.deleteFromCart:', deleteItem);
		if(_log) console.log('CartService.this._cart:', this._cart);

		//let tempCart: Cart = JSON.parse(JSON.stringify(this._cart));
		//tempCart.items = tempCart.items.filter(item => item.productScheduleId != deleteItem.productScheduleId && item.date != deleteItem.date);
		//filter isn't working well matching against 2 pieces of data ^

		let tempCart: Cart = this.getCartDefault();
		tempCart.id = this._cart.id;

		this._cart.items.forEach(item => {
			//console.log('CartService.item:', item);
			//console.log('CartService.compare:', item.productScheduleId, 'vs', deleteItem.productScheduleId, '=', (item.productScheduleId == deleteItem.productScheduleId));
			//console.log('CartService.compare:', item.date, 'vs', deleteItem.date, '=', (item.date == deleteItem.date));
			//console.log('CartService.compare:', (item.productScheduleId != deleteItem.productScheduleId) && (item.date != deleteItem.date));
			if(item.productScheduleId == deleteItem.productScheduleId && item.date == deleteItem.date) {
				//console.log('CartService.found item to delete ^^^^^^^'); //don't add to array
			} else {
				tempCart.items.push(item);
			}
		});

		if(_log) console.log('CartService.deleteFromCart.tempCart.items:', tempCart.items);

		return tempCart;


	}






	/**
	 *
	 *
	 * PAYMENT ORDER INFO - maybe move this to it's own service
	 *
	 *
	*/



	public checkOrderStorage(){

		//todo-jk: IF LOGGEDIN, CHECK API, ELSE GET FROM LOCALSTORAGE

		let _storageOrder = this._storageService.getOrder();
		if(_storageOrder){
			this._order = _storageOrder;
			this._order_Source.next(_storageOrder);
		}
	}

	public getOrder(){
		if(_log) console.log('CartService.getOrder()', this._order);
		return of(this._order);
	}

	public setOrder(order: Order){
		if(_log) console.log('CartService.setOrder()', order);
		this._order = order;
		this._order_Source.next(order);
		//this._storageService.setOrder(order);
	}

	public clearOrder(){
		if(_log) console.log('CartService.clearOrder()');
		this._order = null;
		this._order_Source.next(this._order);
		//this._storageService.unSetOrder();
	}



	/**
	 * Process Order as Observable
	 * Order data is incomplete. Requires calls for Product info and ProductPricingOption info
	 * @param order
	 * @returns
	*/
	public processOrder(includeAdditionalInfo: boolean = false): Observable<any>{//debugger
		if(_log) console.log('CartService.processOrder', this._order);
		return this.order$.pipe(switchMap(c => {
			const order: Order = {...c};
			if(_log) console.log('CartService.processOrder', order);
			order.orderTotal = 0;
			order.cart = {};
			order.additionalInformationComplete = true;
			if(order?.bookings) {
				order.bookings.forEach(async (item: any) => {
					//console.log('CartService.processOrder.item:', item);

					const product = await this._compassAPIProductsService.getProductByIdPromise(item.productId);
					//console.log('CartService.processOrder.product:', product);

					if(product) {
						item.productUrl = this.baseUrl + '/' + product.destination.region.slug + '/' + product.destination.slug + '/' + product.slug;
						item.productUseName = (product.displayName) ? product.displayName : product.name;
						item.productImageUrl = (product?.assets && product.assets.length > 0) ? product.assets[0].url : this._configService.images.defaultImage;

						//* missing schedule from order data*
						// let findSchedule = product.schedules.find(({id}) => id == item.productScheduleId);
						// console.log('CartService.processOrder.findSchedule:', findSchedule);
						// if(findSchedule) item.productScheduleUseName = (findSchedule.displayName) ? findSchedule.displayName : findSchedule.name;

						// item.duration = product.duration;
						// item.startTime = findSchedule.startTime;
						// item.otherStartTime = findSchedule.otherStartTime;

					}

					item.itemTotal = 0;
					item.pricingOptions.forEach(async option => {
						//console.log('---- CartService.processOrder.order.item.option', option);

						const pricingOption = await this._compassAPIProductPricingOptionsService.getProductPricingOptionByIdPromise(option.productPricingOptionId);
						//console.log('CartService.processOrder.pricingOption:', pricingOption);

						option.useName = (pricingOption.displayName) ? pricingOption.displayName : pricingOption.name;
						option.displayPrice = pricingOption.displayPrice;
						//console.log('CartService.processOrder.option:', option);

						order.orderTotal += option.displayPrice;
						order.cart.cartTotal = order.orderTotal;

					});

					if(_log) console.log('CartService.processOrder.order:', order);

					if(includeAdditionalInfo) {
						this.getAdditionalInfo(item, order);
					}

				});


				order.cart.items = order.bookings;

				if(_log) console.log('order:', order);

				return of(order);
			}
			if(_log) console.log('return EMPTY');
			return EMPTY;
		}));
	}





	/**
	 * Get booking additional info form
	 * @param booking
	 */
	public getAdditionalInfo(booking, order) {
		//console.log('CartService.getAdditionalInfo.booking:', booking);
		this._compassAPIBookingAdditionalInfoFormService.getBookingAdditionalInfoForm(booking.id)
			.subscribe((res) => {
				//console.log('CartService.getBookingAdditionalInfoForm.res:', res);
				if(res.id) {
					let isComplete = true;
					res.formFields.forEach(field => {
						if(!field.fieldValue) {
							isComplete = false;
							order.additionalInformationComplete = false;
						}
					});
					booking.additionalInfo = res;
					booking.additionalInfo.isComplete = isComplete;
				}
			});
	}



}
